import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import MainLayout from '@/components/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, 
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Smartphone,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const CaptureBridge = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchWhatsAppStatus();
    fetchActivity();
  }, []);

  const fetchWhatsAppStatus = async () => {
    try {
      const response = await fetch(`${API}/whatsapp/status`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setWhatsappStatus(await response.json());
      }
    } catch (error) {
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await fetch(`${API}/whatsapp/activity?limit=15`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setActivity(await response.json());
      }
    } catch (error) {
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchWhatsAppStatus(), fetchActivity()]);
    setRefreshing(false);
    toast.success('Activity refreshed');
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Not configured';
    // Format as +91 XXXXX XXXXX
    if (phone.length === 12 && phone.startsWith('91')) {
      return `+${phone.slice(0, 2)} ${phone.slice(2, 7)} ${phone.slice(7)}`;
    }
    return phone;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'executed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Executed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'expired':
        return (
          <Badge className="bg-gray-100 text-gray-600 border-gray-200">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Expired
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-gray-100 text-gray-600 border-gray-200">
            <XCircle className="h-3 w-3 mr-1" />
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-gray-600">
            {status}
          </Badge>
        );
    }
  };

  const getIntentLabel = (intent) => {
    const labels = {
      'schedule_session': 'Schedule Session',
      'reschedule_session': 'Reschedule',
      'mark_payment_done': 'Mark Payment'
    };
    return labels[intent] || intent;
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="whatsapp-command-center">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-foreground"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              WhatsApp Command Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your practice via WhatsApp commands
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Activity Feed */}
          <div className="lg:col-span-2 space-y-4">
            {/* Section B: Recent WhatsApp Activity */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  <MessageSquare className="h-5 w-5 inline mr-2 text-primary" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingActivity ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading activity...
                  </div>
                ) : activity.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-40" />
                    <p className="font-medium">No commands yet</p>
                    <p className="text-sm mt-1">
                      Send a command from your verified WhatsApp to get started
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activity.map((item) => (
                      <div 
                        key={item.action_id}
                        className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50"
                        data-testid={`activity-item-${item.action_id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Command Text */}
                            <p className="text-sm text-foreground font-medium truncate">
                              {item.command_text || 'Command'}
                            </p>
                            
                            {/* Meta Info */}
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              {item.patient_name && (
                                <span className="font-medium text-foreground">
                                  {item.patient_name}
                                </span>
                              )}
                              <span className="px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                {getIntentLabel(item.intent)}
                              </span>
                              <span>
                                {item.timestamp && formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                              </span>
                            </div>

                            {/* Result Message */}
                            {item.result_message && item.status === 'executed' && (
                              <p className="text-xs text-emerald-600 mt-2">
                                {item.result_message}
                              </p>
                            )}

                            {/* Session Link */}
                            {item.session_id && (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 mt-2 text-xs text-primary"
                                onClick={() => navigate(`/sessions/${item.session_id}`)}
                                data-testid={`view-session-${item.action_id}`}
                              >
                                View Session
                                <ArrowRight className="h-3 w-3 ml-1" />
                              </Button>
                            )}
                          </div>

                          {/* Status Badge */}
                          <div className="flex-shrink-0">
                            {getStatusBadge(item.status)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Section A: WhatsApp Status Card */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  <Smartphone className="h-5 w-5 inline mr-2 text-primary" />
                  WhatsApp Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingStatus ? (
                  <div className="text-center py-4 text-muted-foreground">
                    Loading...
                  </div>
                ) : (
                  <>
                    {/* WhatsApp Number */}
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        WhatsApp Number
                      </p>
                      <p className="font-medium text-foreground" data-testid="whatsapp-number">
                        {formatPhoneNumber(whatsappStatus?.whatsapp_number)}
                      </p>
                    </div>

                    {/* Verification Status */}
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Verification Status
                      </p>
                      <div data-testid="verification-status">
                        {whatsappStatus?.whatsapp_verified ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Not Verified
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Last Command */}
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">
                        Last Command Received
                      </p>
                      <p className="text-sm text-foreground" data-testid="last-command">
                        {whatsappStatus?.last_command_at 
                          ? format(new Date(whatsappStatus.last_command_at), 'MMM d, yyyy h:mm a')
                          : 'No commands yet'
                        }
                      </p>
                    </div>

                    {/* Configure Button */}
                    {!whatsappStatus?.whatsapp_verified && (
                      <Button 
                        variant="outline" 
                        className="w-full rounded-xl mt-2"
                        onClick={() => navigate('/settings')}
                        data-testid="configure-whatsapp-btn"
                      >
                        Configure WhatsApp
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Section C: Supported Commands Reference */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: 'Fraunces, serif' }}>
                  Supported Commands
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Schedule Session */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    Schedule Session
                  </p>
                  <div className="space-y-1 pl-4">
                    <p className="p-2 rounded bg-muted/50 font-mono text-xs">
                      Schedule session for Priya tomorrow 6pm
                    </p>
                    <p className="p-2 rounded bg-muted/50 font-mono text-xs">
                      Schedule intro for Rahul on Monday 10am
                    </p>
                  </div>
                </div>

                {/* Reschedule */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    Reschedule Session
                  </p>
                  <div className="space-y-1 pl-4">
                    <p className="p-2 rounded bg-muted/50 font-mono text-xs">
                      Reschedule Priya to Thursday 5pm
                    </p>
                    <p className="p-2 rounded bg-muted/50 font-mono text-xs">
                      Move Rahul's session to next week Monday
                    </p>
                  </div>
                </div>

                {/* Mark Payment */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Mark Payment Done
                  </p>
                  <div className="space-y-1 pl-4">
                    <p className="p-2 rounded bg-muted/50 font-mono text-xs">
                      Mark payment done for Priya
                    </p>
                    <p className="p-2 rounded bg-muted/50 font-mono text-xs">
                      Payment received from Rahul
                    </p>
                  </div>
                </div>

                {/* How to Confirm */}
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">
                    How to Confirm
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                        1
                      </div>
                      <p className="text-sm text-foreground pt-0.5">
                        Send command from your verified WhatsApp
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                        2
                      </div>
                      <p className="text-sm text-foreground pt-0.5">
                        Review the confirmation message
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
                        3
                      </div>
                      <p className="text-sm text-foreground pt-0.5">
                        Reply <span className="font-semibold">YES</span> to execute
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CaptureBridge;
